import React, { useState, useRef, useEffect } from 'react';
import {
  Badge, Button, Form, Modal, Table, Alert,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { format, parseISO } from 'date-fns';
import { Navbar } from '../../../components/Navbar';
import { Sidebar } from '../../../components/Sidebar';
import useAdvogadosConfiguracoes from '../../../services/api/advogadosConfiguracoes';

const emptyForm = {
  nome: '',
  cpf: '',
  sexo: '',
  email: '',
  telefone: '',
  logradouro: '',
  numero: '',
  bairro: '',
  cidade: '',
  estado: '',
  dataNascimento: null, // Date | null
  ativo: true,
};

export default function AdvogadosConfiguracoes() {
  const [submitting, setSubmitting] = useState(false);

  const normalizeMsg = (msg) => {
    if (!msg) return '';
    if (typeof msg === 'string') return msg;
    if (typeof msg?.message === 'string') return msg.message;
    if (typeof msg?.mensagem === 'string') return msg.mensagem;
    try { return JSON.stringify(msg); } catch { return String(msg); }
  };

  const [floatAlert, setFloatAlert] = useState({
    isVisible: false,
    message: '',
    variant: 'success',
  });

  const floatTimerRef = useRef(null);

  const showFloatAlert = (message, variant = 'success') => {
    if (floatTimerRef.current) clearTimeout(floatTimerRef.current);

    setFloatAlert({ isVisible: true, message: normalizeMsg(message), variant });
    floatTimerRef.current = setTimeout(() => {
      setFloatAlert({ isVisible: false, message: '', variant: 'success' });
      floatTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => () => {
    if (floatTimerRef.current) clearTimeout(floatTimerRef.current);
  }, []);

  const [show, setShow] = useState(false);
  const [mode, setMode] = useState('create'); // create | edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Acesso já é gated na rota (routesAdm.jsx, requiresPermissao=
  // "configuracoes.advogados") - não repetir a checagem aqui, senão quem
  // ganhar a permissão por um setor fora de Gestão fica preso de novo.

  const {
    advogados,
    isLoading,
    isError,
    criarAdvogado,
    editarAdvogado,
  } = useAdvogadosConfiguracoes();

  const isBusy = submitting || isLoading;

  const openCreate = () => {
    setMode('create');
    setEditingId(null);
    setForm(emptyForm);
    setShow(true);
  };

  const openEdit = (o) => {
    setMode('edit');
    setEditingId(o.id);
    setForm({
      nome: o.nome || '',
      cpf: o.cpf || '',
      sexo: o.sexo || '',
      email: o.email || '',
      telefone: o.telefone || '',
      logradouro: o.logradouro || '',
      numero: o.numero || '',
      bairro: o.bairro || '',
      cidade: o.cidade || '',
      estado: o.estado || '',
      cep: o.cep || '',
      dataNascimento: o.datanascimento ? parseISO(String(o.datanascimento).slice(0, 10)) : null,
      ativo: !!o.ativo,
    });
    setShow(true);
  };

  const validate = () => {
    if (!form.nome.trim()) return 'Informe o nome.';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) {
      showFloatAlert(err, 'danger');
      return;
    }

    const payload = {
      nome: form.nome.trim().toUpperCase(),
      cpf: form.cpf?.trim() || null,
      sexo: form.sexo?.trim() || null,
      email: form.email?.trim() || null,
      telefone: form.telefone?.trim() || null,
      logradouro: form.logradouro?.trim() || null,
      numero: form.numero?.trim() || null,
      bairro: form.bairro?.trim() || null,
      cidade: form.cidade?.trim() || null,
      estado: form.estado?.trim()?.toUpperCase() || null,
      cep: form.cep?.trim() || null,
      datanascimento: form.dataNascimento ? format(form.dataNascimento, 'yyyy-MM-dd') : null,
      ativo: !!form.ativo,
    };

    setSubmitting(true);
    try {
      if (mode === 'create') {
        await criarAdvogado(payload);
      } else {
        await editarAdvogado(editingId, payload);
      }

      setShow(false);
      setEditingId(null);
      setForm(emptyForm);
      showFloatAlert('Dados salvos com sucesso!', 'success');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Erro ao salvar advogado:', e);

      const status = e?.response?.status;
      const apiMsg = e?.response?.data?.mensagem
        || e?.response?.data?.message
        || e?.response?.data?.error;

      const msg = apiMsg
        || (status ? `Falha ao salvar (HTTP ${status}).` : null)
        || e?.message
        || 'Não foi possível salvar o advogado.';

      showFloatAlert(msg, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {floatAlert.isVisible && (
        <div className="fixed-top mt-5" style={{ zIndex: 3000 }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-6">
                <Alert
                  variant={floatAlert.variant}
                  onClose={() => setFloatAlert({ isVisible: false, message: '', variant: 'success' })}
                  dismissible
                >
                  <p className="text-center mt-3">{floatAlert.message}</p>
                </Alert>
              </div>
            </div>
          </div>
        </div>
      )}

      <div id="wrapper">
        <Sidebar />

        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            <Navbar />

            <div className="container-fluid">
              <div className="d-sm-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0 text-gray-800">Advogados - Configurações</h1>

                <Button variant="success" onClick={openCreate} disabled={isBusy}>
                  Criar novo advogado
                </Button>
              </div>

              <hr />

              <div className="card shadow mb-4">
                <div className="card-header py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <strong className="m-0">Lista de Advogados</strong>
                </div>

                <div className="card-body">
                  <Table responsive size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '24%' }}>Nome</th>
                        <th style={{ width: '18%' }}>CPF</th>
                        <th style={{ width: '22%' }}>Email</th>
                        <th style={{ width: '18%' }}>Telefone</th>
                        <th style={{ width: '10%' }} className="text-center">
                          Ativo
                        </th>
                        <th style={{ width: '8%' }} className="text-center">
                          Ações
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            Carregando...
                          </td>
                        </tr>
                      ) : isError ? (
                        <tr>
                          <td colSpan={6} className="text-center text-danger py-4">
                            Erro ao carregar advogados.
                          </td>
                        </tr>
                      ) : (advogados || []).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            Nenhum advogado cadastrado.
                          </td>
                        </tr>
                      ) : (
                        advogados.map((o) => (
                          <tr key={o.id}>
                            <td>{String(o.nome || '').toUpperCase()}</td>
                            <td className="text-muted">{o.cpf || '-'}</td>
                            <td className="text-muted">{o.email || '-'}</td>
                            <td className="text-muted">{o.telefone || '-'}</td>

                            <td className="text-center">
                              <Badge bg={o.ativo ? 'success' : 'secondary'}>
                                {o.ativo ? 'Ativo' : 'Inativo'}
                              </Badge>
                            </td>

                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => openEdit(o)}
                                  disabled={isBusy}
                                >
                                  Editar
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              <Modal
                show={show}
                onHide={() => {
                  if (isBusy) return;
                  setShow(false);
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                centered
              >
                <Modal.Header closeButton={!isBusy}>
                  <Modal.Title>
                    {mode === 'create' ? 'Criar novo advogado' : 'Editar advogado'}
                  </Modal.Title>
                </Modal.Header>

                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (isBusy) return;
                    save();
                  }}
                >
                  <Modal.Body>
                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Nome:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: João da Silva"
                        value={form.nome}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          nome: e.target.value.toUpperCase(),
                        }))}
                        maxLength={80}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">CPF:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: 000.000.000-00"
                        value={form.cpf}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          cpf: e.target.value,
                        }))}
                        maxLength={20}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Sexo:</Form.Label>
                      <Form.Select
                        value={form.sexo}
                        onChange={(e) => setForm((prev) => ({ ...prev, sexo: e.target.value }))}
                        disabled={isBusy}
                      >
                        <option value="">Selecione...</option>
                        <option value="M">Masculino</option>
                        <option value="F">Feminino</option>
                        <option value="O">Outro</option>
                      </Form.Select>
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Email:</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Ex: email@dominio.com"
                        value={form.email}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))}
                        maxLength={120}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Telefone:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: (11) 99999-9999"
                        value={form.telefone}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          telefone: e.target.value,
                        }))}
                        maxLength={30}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Logradouro:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: Rua das Flores"
                        value={form.logradouro}
                        onChange={(e) => setForm((prev) => ({ ...prev, logradouro: e.target.value }))}
                        maxLength={120}
                        disabled={isBusy}
                      />
                    </div>
                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Número:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: 123"
                        value={form.numero}
                        onChange={(e) => setForm((prev) => ({ ...prev, numero: e.target.value }))}
                        maxLength={20}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Bairro:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: Centro"
                        value={form.bairro}
                        onChange={(e) => setForm((prev) => ({ ...prev, bairro: e.target.value }))}
                        maxLength={80}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Cidade:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: São Paulo"
                        value={form.cidade}
                        onChange={(e) => setForm((prev) => ({ ...prev, cidade: e.target.value }))}
                        maxLength={80}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Estado (UF):</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: SP"
                        value={form.estado}
                        onChange={(e) => setForm((prev) => ({ ...prev, estado: e.target.value.toUpperCase() }))}
                        maxLength={2}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">CEP:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: 00000-000"
                        value={form.cep}
                        onChange={(e) => setForm((prev) => ({ ...prev, cep: e.target.value }))}
                        maxLength={10}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold d-block mb-1">
                        Data de nascimento:
                      </Form.Label>

                      <div className="d-block">
                        <DatePicker
                          selected={form.dataNascimento}
                          onChange={(date) => setForm((prev) => ({ ...prev, dataNascimento: date }))}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="DD/MM/AAAA"
                          showYearDropdown
                          showMonthDropdown
                          showPopperArrow={false}
                          disabled={isBusy}
                          wrapperClassName="w-100"
                          customInput={(
                            <input
                              className="form-control"
                              style={{ width: '100%' }}
                              placeholder="DD/MM/AAAA"
                            />
                        )}
                        />
                      </div>
                    </div>

                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => {
                        if (isBusy) return;
                        setShow(false);
                        setEditingId(null);
                        setForm(emptyForm);
                      }}
                      disabled={isBusy}
                    >
                      Cancelar
                    </Button>

                    <Button variant="primary" type="submit" disabled={isBusy}>
                      {submitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Modal.Footer>
                </Form>
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
